/* global d3,Spinner */

(function (global) {
  'use strict';

  // utility function to limit a value to min and/or max
  var limitValue = function limitValue(value, limit) {
    // limit = {min: minLimit, max: maxLimit}
    if (typeof value === 'undefined') {
      return NaN;
    }
    if (limit.min) {
      value = Math.max(value, limit.min);
    }
    if (limit.max) {
      value = Math.min(value, limit.max);
    }
    return value;
  };
  var chartRenderers = {
    "interaction-timeline": renderInteractionTimeline,
    "matching": renderMatching,
    "question-bullet": renderQuestionBullet,
    "hierarchy": renderHierarchy,
    "network": renderNetwork
  };
  function render(chartConfig) {
    chartRenderers[chartConfig.type](chartConfig);
  }

  // "global" used to track a delayed spinner (using setTimeout) created on a prior
  // render cycle that might need to be stopped in the current render cycle
  var delayedSpinner = null;
  var loadingSpinner = new Spinner({
    lines: 12,
    // The number of lines to draw
    length: 8,
    // The length of each line
    width: 4,
    // The line thickness
    radius: 10,
    // The radius of the inner circle
    scale: 1.0,
    // Scales overall size of the spinner
    corners: 1,
    // Roundness (0..1)
    color: '#000',
    // #rgb or #rrggbb
    opacity: 1 / 4,
    // Opacity of the lines
    rotate: 0,
    // Rotation offset
    direction: 1,
    // 1: clockwise, -1: counterclockwise
    speed: 0.83,
    // Rounds per second
    trail: 50,
    // Afterglow percentage
    fps: 20,
    // Frames per second when using setTimeout()
    zIndex: 2e9,
    // Use a high z-index by default
    className: 'spinner',
    // CSS class to assign to the element
    top: '50%',
    // center vertically
    left: '50%',
    // center horizontally
    shadow: false,
    // Whether to render a shadow
    hwaccel: false,
    // Whether to use hardware acceleration (might be buggy)
    position: 'absolute' // Element positioning
  });

  /* Configurables:
   * - chartConfig.container  (string or element)
   * - chartConfig.width      (number: pixels)    default: container width, or 580
   * - chartConfig.height     (number: pixels)    default: container height, or 480
   * - chartConfig.data {     (a node object representing the root of a tree structure with fields:)
   *                          (--- only listing fields currently used by this chart ---)
   *      children        (array of node objects),
   *      id              (integer),
   *      name            (string),
   *      people          (array of person objects - only uses people.length),
   *      type  {         (object representing hierarchy group -- only using field 'name')
   *          name        (string)
   *      }
   *   }
   * - chartConfig.clickFn        (function - input of selected node)
   * - chartConfig.duration       (number: milliseconds)  default: 750
   * - chartConfig.layoutOpts {
   *      heightFactor     (number),  default: 1.0 - fraction of available height to use for layout
   *      widthScale       (number),  default: 6.0 - scale factor to convert label length (chars) to tree level width (pixel)
   *      widthPadding     (number),  default: 50 - additional width in pixels added to level when only 1 label between levels
   *      nodeSpacing {
   *          min         (number: pixels),   default: 15 - minimum vertical spacing of nodes
   *          max         (number: pixels)    default: 100 - maximum vertical spacing of nodes
   *      },
   *      levelWidth {
   *          min         (number: pixels),   default: 75 - minimum horizontal spacing between tree levels
   *          max         (number: pixels)    default: 500 - maximum horizontal spacing between tree levels
   *      },
   *      maxFanOutRatio  (number),   default 1.0 - a number between 0.5 and 5 (typically).
   *                                  maxFanOutRatio is an upper limit on the ratio of maxFanOut (px) to levelWidth (px).
   *                                  It can help make graphs with a large vertical span (e.g. root node of a wide tree) or a large
   *                                  number of children look better by increasing levelWidth. Larger values allow larger fanOuts,
   *                                  smaller values will stretch the graph horizontally as needed, within levelWidth limits.
   *      zoomLevel {
   *          min         (number),   default: 0.1
   *          max         (number),   default: 3.0
   *          initial     (number)    default: null (auto) - a value between zoom.min and zoom.max
   *      }
   *   }
   * - chartConfig.tooltipLabel   (function - input of selected nodeId, output of text string)
   *
   */
  function renderHierarchy(chartConfig) {
    // Configuration variables
    var chartContainer = d3.select(chartConfig.container),
      ew = (chartConfig.width || chartContainer.style('width')).replace('px', '') || 580,
      eh = (chartConfig.height || chartContainer.style('height')).replace('px', '') || 480;

    // Tree layout defaults - aesthetically driven
    var layoutOptDefaults = {
      heightFactor: 1.0,
      // fraction of available height to use for initial tree height
      widthScale: 6.0,
      // scale factor to convert label length (chars) to tree level width (pixel)
      widthPadding: 50,
      // extra space in pixels added to level when only 1 label between levels
      nodeSpacing: {
        min: 15,
        max: 100
      },
      // vertical spacing between nodes
      levelWidth: {
        min: 75,
        max: 500
      },
      // horizontal spacing between tree treeLayout
      maxFanOutRatio: 1.0,
      // max ratio of fanOut to width
      zoomLevel: {
        min: 0.1,
        max: 3.0,
        initial: null
      } // zoom level extents
    };
    var layoutOpts = {};
    $.extend(true, layoutOpts, layoutOptDefaults, chartConfig.layoutOpts || {});

    /*
     *  d3 tree layout is top-down. In the layout space, root is placed at [width/2, 0].
     *  Sibling nodes have different x-coordinates, but the same y-coordinate.
     *  An increase in y-coordinate represent an increase in tree "depth/level".
     *
     *  To display the tree "sideways", transpose use of width & height.
     */

    // Data - define the root node and its coordinates
    var root = chartConfig.data || {};
    root.x0 = eh / 2; // middle of window vertically
    root.y0 = 0; // left edge of window

    // Misc. variables
    var uid = 0; // used by d3 key function to create element id's
    var duration = chartConfig.duration || 750; // transition duration

    /*
     *  Resize callback - assumes that nothing has changed except the size of the container.
     */
    chartConfig.resize = function () {
      ew = chartContainer.style('width').replace('px', '');
      eh = chartContainer.style('height').replace('px', '');
      d3.select("svg").attr("width", ew).attr("height", eh);
      zoomListener.center([ew / 2, eh / 2]); // reset zoom center
      var temp = duration;
      duration = 0; // render immediately
      updateTree(root);
      centerTree();
      duration = temp;
    };

    /*
     *  Initialize the chart container and setup the tooltip
     */

    // Clear out container
    chartContainer.html(null);

    // setup tooltip container
    chartContainer.style('position', 'relative').append('div').attr('class', 'd3tooltip').append('span').attr('class', 'value');

    // tooltip object
    var tooltip = {
      element: chartContainer.select('.d3tooltip'),
      visible: false,
      offsetX: 6,
      offsetY: 7,
      // configurable tooltip label
      tooltipLabel: chartConfig.tooltipLabel || function (node) {
        var verb = this.isCollapsed ? 'expand' : 'collapse',
          text = this.hasChildren ? 'Click to ' + verb + ' this node' : 'This is a leaf node';
        return '<span>' + text + '</span>';
      },
      // set tooltip label, position, and show it
      show: function show(node) {
        this.nodeX = node.x;
        this.nodeY = node.y;
        this.hasChildren = node.children || node._children;
        this.isCollapsed = node._children ? true : false;
        this.element.select('.value').html(this.tooltipLabel(node));
        this.width = parseInt(this.element.style('width')); // save values for use in zoom()
        this.height = parseInt(this.element.style('height'));
        this.visible = true;
        this.zoom();
        this.element.transition().style('opacity', 1);
      },
      hide: function hide() {
        this.element.transition().style('opacity', 0);
        this.visible = false;
      },
      zoom: function zoom() {
        // set tooltip position based on tree zoom level
        if (this.visible) {
          // only do it if the tooltip is visible
          var translate = zoomListener.translate(),
            scale = zoomListener.scale(),
            offsetX = this.hasChildren ? this.offsetX : -this.offsetX,
            x = (this.nodeY - offsetX) * scale + translate[0] - (this.hasChildren ? this.width : 0),
            y = (this.nodeX - this.offsetY) * scale + translate[1] - this.height;
          this.element.style('left', x + 'px');
          this.element.style('top', y + 'px');
        }
      }
    };

    /*
     *  Setup baseSvg and define the zoom listener for the chart
     */

    // define the zoomListener which calls the zoom function on the "zoom" event constrained by layoutOpts.zoomLevel
    var zoomListener = d3.behavior.zoom().center([ew / 2, eh / 2]) // set zoom center
    .scaleExtent([layoutOpts.zoomLevel.min, layoutOpts.zoomLevel.max]).on("zoom", zoom);

    // define the baseSvg, attaching a class for styling and the zoomListener
    var baseSvg = d3.select("#org-hierarchy").append("svg").attr("width", ew).attr("height", eh).attr("class", "overlay").call(zoomListener);

    // Append a group which holds all nodes and which the zoom Listener can act upon.
    var svgGroup = baseSvg.append("g");

    // Define the zoom function for the tree
    function zoom() {
      if (d3.event.sourceEvent.type === 'dblclick') {
        // reset tree to center, initial zoom
        tooltip.hide();
        centerTree();
      } else {
        svgGroup.attr("transform", "translate(" + d3.event.translate + ")scale(" + d3.event.scale + ")");
        tooltip.zoom();
      }
    }

    // show/hide loadingSpinner
    function updateSpinner(loading) {
      if (delayedSpinner) {
        window.clearTimeout(delayedSpinner);
      }
      loadingSpinner.stop();
      if (loading) {
        delayedSpinner = window.setTimeout(function () {
          loadingSpinner.spin(chartContainer[0][0]);
        }, 750);
      }
    }

    /*
     *  treeLayout - used to hold layout dimensions of the tree
     */
    var treeLayout = {
      levels: [],
      // used to set widths of each tree level
      nodeSpacing: 0,
      // used to space nodes vertically
      height: 0,
      // the rest used by centering function...
      width: 0,
      xMax: 0,
      xMin: 0,
      // initialize new treeLayout level
      newLevel: function newLevel() {
        return {
          maxLabel: [0, 0],
          // max labels for this level [left-label, right-label]
          maxFanOut: 0,
          // max fanOut of children from single parent at prev level
          fanOut: null,
          // temp used to calc maxFanOut
          width: 0,
          // level width (pixels)
          y: 0 // y-coordinate (horizontal) of nodes at this level
        };
      },
      // extend levels array
      extend: function extend(depth) {
        var level = this.levels.length;
        while (level++ <= depth) {
          // append to array until level = depth + 1
          this.levels.push(this.newLevel());
        }
      },
      // clear layout data
      clear: function clear() {
        this.levels = []; // empty the array
        this.nodeSpacing = 0; // clear previous layout values
        this.height = 0;
        this.width = 0;
        this.xMax = 0;
        this.xMin = 0;
      },
      get: function get(level) {
        if (level > this.levels.length - 1) {
          this.extend(level);
        }
        return this.levels[level];
      },
      getY: function getY(level) {
        return this.levels[level].y;
      }
    };

    /*
     *  Create and process the tree
     */
    var tree = d3.layout.tree(root); // create the tree, run preliminary layout
    var nodes = null,
      links = null;
    sortTree();
    updateTree(root);
    centerTree();
    updateSpinner(chartConfig.loading);

    /*
     *  updateTreeLayout - evaluate tree data by levels (depth) and calculate layout dimensions
     *
     *  NOTE: updateTreeLayout assumes that empty children arrays are represented by children = null.
     *  This can be accomplished by running a tree layout, as in "d3.layout.tree(root)" above.
     *
     *  Algorithm:
     *  1) Analyze the tree
     *     - find the maxLabelLength between nodes (chars) & tree height vertically (number of nodes).
     *          to get the height in nodes, we set the nodeSize to [1, 1] so each node height=1.
     *     - also find the maxFanOut at each level.  This is used to adjust the layout.
     *  2) Calculate the layout:
     *     - Calculate nodeSpacing so that numRows of the tree will fill up heightFactor * screenHeight (eh)
     *          - impose nodeSpacing min/max
     *     - Set width between levels to maxLabelLength * widthFactor (ideally, no overlap of labels/nodes)
     *          - if maxFanOut/levelWidth > maxFanOutRatio, add width to lower the ratio
     *          - impose levelWidth min/max
     */
    function updateTreeLayout() {
      // calculate the label length (chars) for node
      function getLabelLength(node) {
        // label = name +  " - " + typeName
        return node.name ? node.name.length + (node.type && node.type.name ? node.type.name.length + 3 : 0) : 0;
      }

      // initialize tree layout
      tree.nodeSize([1, 1]) // set nodeSpacing = 1 to calculate height of tree in nodes
      .nodes(root); // run an initial tree layout
      treeLayout.xMax = treeLayout.xMin = root.x;

      // visit all nodes at each level to find max label widths and node count at each level
      function analyzeLevel(level, node) {
        var thisLevel = treeLayout.get(level),
          nextLevel = treeLayout.get(level + 1),
          labelLength = getLabelLength(node);
        treeLayout.xMax = Math.max(node.x, treeLayout.xMax);
        treeLayout.xMin = Math.min(node.x, treeLayout.xMin);
        if (thisLevel.fanOut) {
          thisLevel.fanOut.max = Math.max(node.x, thisLevel.fanOut.max);
          thisLevel.fanOut.min = Math.min(node.x, thisLevel.fanOut.min);
        } else {
          thisLevel.fanOut = {
            max: node.x,
            min: node.x
          }; // initialize fanOut
        }
        if (node.children || node._children) {
          // if children, label goes on this level
          thisLevel.maxLabel[1] = Math.max(labelLength, thisLevel.maxLabel[1]);
          if (node.children) {
            // process visible children
            nextLevel.fanOut = null; // initialize fanOut values from this parent
            node.children.forEach(function (node) {
              analyzeLevel(level + 1, node); // visit visible nodes in next level
            });
            if (nextLevel.fanOut === null) {
              console.log('Error calculating fanOut in updateTreeLayout()');
            }
            var fanOut = nextLevel.fanOut.max - nextLevel.fanOut.min + 1;
            nextLevel.maxFanOut = Math.max(fanOut, nextLevel.maxFanOut);
          }
        } else {
          // no children, label goes on next level
          nextLevel.maxLabel[0] = Math.max(labelLength, nextLevel.maxLabel[0]);
        }
      }

      // aggregate level data to determine layout dimensions
      function calculateLayout() {
        var numRows = treeLayout.xMax - treeLayout.xMin + 1; // layout was initialized with nodeSpacing = 1
        var nodeSpacing = eh * layoutOpts.heightFactor / numRows; // calculate vertical spacing from available height
        nodeSpacing = limitValue(nodeSpacing, layoutOpts.nodeSpacing); // impose layout limits

        treeLayout.nodeSpacing = nodeSpacing;
        treeLayout.height = nodeSpacing * numRows;
        treeLayout.xMax *= nodeSpacing; // scale values by nodeSpacing
        treeLayout.xMin *= nodeSpacing;
        treeLayout.levels.forEach(function (level) {
          var labelWidth = level.maxLabel[0] + level.maxLabel[1];
          var levelWidth = labelWidth * layoutOpts.widthScale + (level.maxLabel[0] && level.maxLabel[1] ? 0 : layoutOpts.widthPadding);
          if (levelWidth === 0 || level.maxFanOut * nodeSpacing / levelWidth > layoutOpts.maxFanOutRatio) {
            levelWidth = level.maxFanOut * nodeSpacing / layoutOpts.maxFanOutRatio;
          }
          level.width = limitValue(levelWidth, layoutOpts.levelWidth);
          level.y = treeLayout.width += level.width;
        });
      }
      treeLayout.clear(); // clear prior layout data
      analyzeLevel(0, root); // analyze each level recursively
      calculateLayout(); // calculate layout from level data
    }

    /*
     *  Support functions
     */
    function sortTree() {
      tree.sort(function (a, b) {
        return b.name.toLowerCase() < a.name.toLowerCase() ? 1 : -1;
      });
    }
    function centerNode(node) {
      var zoomLevel = zoomListener.scale();
      var x = -node.y0;
      var y = -node.x0;
      x = x * zoomLevel + ew / 2;
      y = y * zoomLevel + eh / 2;
      svgGroup.transition().duration(duration).attr("transform", "translate(" + x + "," + y + ")scale(" + zoomLevel + ")");
      zoomListener.scale(zoomLevel);
      zoomListener.translate([x, y]);
    }
    function getInitialZoomLevel() {
      var zoomLevel = layoutOpts.zoomLevel.initial;
      if (zoomLevel === null) {
        // get auto zoomLevel
        zoomLevel = Math.min(eh / treeLayout.height, ew / treeLayout.width);
        zoomLevel = limitValue(zoomLevel, {
          min: layoutOpts.zoomLevel.min,
          max: 1.0
        });
      }
      return zoomLevel;
    }
    function getZoomLevel() {
      return zoomListener.scale();
    }

    // center the tree and return to initial zoom (unless initial = auto)
    function centerTree() {
      var zoomLevel = getInitialZoomLevel(),
        treeWidth = treeLayout.width,
        // todo: center on "visible" width of tree
        x = treeWidth / 2,
        // middle of width
        y = (treeLayout.xMax + treeLayout.xMin) / 2; // average of xMax & xMin

      x = -x * zoomLevel + ew / 2;
      y = -y * zoomLevel + eh / 2;
      svgGroup.transition().duration(duration).attr("transform", "translate(" + x + "," + y + ")scale(" + zoomLevel + ")");
      zoomListener.scale(zoomLevel);
      zoomListener.translate([x, y]);
    }

    // pivot the tree around the fixed position of selected node
    function pivotTree(node, prevLoc) {
      var zoomLevel = getZoomLevel(),
        current = zoomListener.translate(),
        x = 0,
        y = 0;
      if (node && prevLoc && prevLoc.length == 2) {
        x = current[0] - (node.y - prevLoc[1]) * zoomLevel;
        y = current[1] - (node.x - prevLoc[0]) * zoomLevel;
        svgGroup.transition().duration(duration).attr("transform", "translate(" + x + "," + y + ")scale(" + zoomLevel + ")");
        zoomListener.scale(zoomLevel);
        zoomListener.translate([x, y]);
      }
    }

    /*
     *  Configurable click function
     */
    var clickFn = chartConfig.clickFn || function (node) {
      node = toggleChildren(node);
      var prevX = node.x,
        prevY = node.y;
      updateTree(node);
      pivotTree(node, [prevX, prevY]);
    };

    // Handle clicking on a node
    var timer = undefined;
    function click(node) {
      tooltip.hide();

      // do not process double-clicks...
      if (typeof timer == "undefined") {
        // click
        timer = setTimeout(function () {
          timer = undefined;
          clickFn(node); // call the click function
        }, 250);
      } else {
        // double-clicks are handled in zoom handler
        clearTimeout(timer);
        timer = undefined;
      }
    }

    // Toggle children function
    function toggleChildren(node) {
      if (node.children) {
        node._children = node.children;
        node.children = null;
      } else if (node._children) {
        node.children = node._children;
        node._children = null;
      }
      return node;
    }

    // Helper functions for collapsing and expanding nodes.
    function collapse(node) {
      if (node.children) {
        node._children = node.children;
        node._children.forEach(collapse);
        node.children = null;
      }
    }
    function expand(node) {
      if (node._children) {
        node.children = node._children;
        node.children.forEach(expand);
        node._children = null;
      }
    }

    // process changes to the tree structure (expand, collapse, etc)
    function updateTree(source) {
      updateTreeLayout();
      //tree = tree.size([treeLayout.height, ew]);  // transpose meaning of x & y in tree
      tree = tree.nodeSize([treeLayout.nodeSpacing, 1]); // adjust tree node height

      // Update tree layout with new size set above and any node changes
      nodes = tree.nodes(root);
      links = tree.links(nodes);

      // Set y-coordinate of each node using layout created by updateTreeLayout()
      nodes.forEach(function (node) {
        node.y = treeLayout.getY(node.depth);
      });

      // define a d3 diagonal projection for use by the node paths later on.
      var diagonal = d3.svg.diagonal().projection(function (node) {
        return [node.y, node.x];
      });

      // Update the nodes
      var node = svgGroup.selectAll("g.node").data(nodes, function (node) {
        return node.id || (node.id = ++uid);
      });

      // Enter any new nodes at the parent's previous position.
      var nodeEnter = node.enter().append("g").attr("class", "node").attr("transform", function () {
        return "translate(" + source.y0 + "," + source.x0 + ")";
      }).on('click', click) // set click handler for each node
      .on('mouseover', function (node) {
        tooltip.show(node);
      }) // set tooltip mouse handlers
      .on('mouseout', function () {
        tooltip.hide();
      });
      nodeEnter.append("circle").attr('class', 'nodeCircle').attr("r", 0).style("fill", function (node) {
        return node._children ? "lightsteelblue" : "#fff";
      });
      nodeEnter.append("text").attr("x", function (node) {
        return node.children || node._children ? -10 : 10;
      }).attr("y", function (node) {
        //check if the node is between other nodes (has parents and children)
        if (node.children && node.parent) {
          // get larger of vertical space between node above & node below
          var siblings = node.parent.children,
            idx = _.indexOf(siblings, node),
            above = idx > 0 ? node.x - siblings[idx - 1].x : Infinity,
            below = idx < siblings.length - 1 ? siblings[idx + 1].x - node.x : Infinity;
          return below > above ? 8 : -8;
        }
        return 0;
      }).attr("dy", ".35em").attr("id", function (node) {
        return node.name ? node.name.replace(/\s/g, "-") : "";
      }).attr('class', 'nodeText').attr("text-anchor", function (node) {
        return node.children || node._children ? "end" : "start";
      }).text(function (node) {
        return node.name;
      }).style("fill-opacity", 0).append("tspan").text(function (node) {
        return node.type && node.type.name ? " - " + node.type.name : "";
      });

      // Change the circle fill depending on whether it has children and is collapsed
      node.select("circle.nodeCircle").attr("r", 4.5).style("fill", function (node) {
        return node._children ? "lightsteelblue" : "#fff";
      });

      // Transition nodes to their new position.
      var nodeUpdate = node.transition().duration(duration).attr("transform", function (node) {
        return "translate(" + node.y + "," + node.x + ")";
      });

      // Fade the text in
      nodeUpdate.select("text").style("fill-opacity", 1);

      // Transition exiting nodes to the parent's new position.
      var nodeExit = node.exit().transition().duration(duration).attr("transform", function () {
        return "translate(" + source.y + "," + source.x + ")";
      }).remove();
      nodeExit.select("circle").attr("r", 0);
      nodeExit.select("text").style("fill-opacity", 0);

      // Update the links
      var link = svgGroup.selectAll("path.link").data(links, function (node) {
        return node.target.id;
      });

      // Enter any new links at the parent's previous position.
      link.enter().insert("path", "g").attr("class", "link").attr("d", function () {
        var o = {
          x: source.x0,
          y: source.y0
        };
        return diagonal({
          source: o,
          target: o
        });
      });

      // Transition links to their new position.
      link.transition().duration(duration).attr("d", diagonal);

      // Transition exiting nodes to the parent's new position.
      link.exit().transition().duration(duration).attr("d", function () {
        var o = {
          x: source.x,
          y: source.y
        };
        return diagonal({
          source: o,
          target: o
        });
      }).remove();

      // Stash the old positions for transition.
      nodes.forEach(function (node) {
        node.x0 = node.x;
        node.y0 = node.y;
      });
    }
  }

  /* Configurables:
   * - chartConfig.container  (string or element)
   * - chartConfig.width      (number: pixels)
   * - chartConfig.height     (number: pixels)
   * - chartConfig.data       (object with fields 'value' (number), and 'max' (number))
   */
  function renderQuestionBullet(chartConfig) {
    // Global variables and whatnot
    var chartContainer = d3.select(chartConfig.container),
      ew = (chartConfig.width || chartContainer.style('width')).replace('px', ''),
      eh = (chartConfig.height || chartContainer.style('height')).replace('px', '');

    // Data
    var data = chartConfig.data || {
      value: 0,
      max: 300
    };

    // Clear out container
    chartContainer.html(null);

    // Set up chart container
    var svgContainer = chartContainer.append('svg').attr('height', eh).attr('width', ew).attr('class', 'd3-chart question-bullet-chart');
    function cx() {
      return ew / 2;
    }

    // Circle Scale
    var rScale = d3.scale.linear().domain([0, data.max]).range([2, eh * 0.5]);
    function cy() {
      return eh / 2;
    }

    // Draw big circles
    svgContainer.append('g').attr('class', 'big-circles').append('circle').attr('class', 'emphasize').attr('cx', cx).attr('cy', cy).attr('r', function () {
      return rScale(data.value);
    });

    // Draw little circles
    svgContainer.append('g').attr('class', 'little-circles').append('circle').attr('cx', cx).attr('cy', cy).attr('r', function () {
      return 2;
    });
  }

  /* Configurables:
   * - chartConfig.container (string or element)
   * - chartConfig.width (number: pixels)
   * - chartConfig.height (number: pixels)
   * - chartConfig.outerPadding (number: pixels)
   * - chartConfig.extraLabelMargin (number: pixels) used by Interaction Timeline to add top margin to month labels
   * - chartConfig.data (array of objects with fields 'date'(ISO8601 UTC string), 'total'(number), 'exactMatches'(number) and 'emphasize'(boolean)
   * - chartConfig.tooltipLabel (function - input of data point from data, output of string)
   * - chartConfig.maxInteractions (number: interaction count) used to determine the top end of the scale for interaction
   *                                                      bubbles.
   */
  function renderInteractionTimeline(chartConfig) {
    /*
     To-dos:
     - Prevent clipping of big circles
     - Animate growing of circles
     */

    // Global variables and whatnot
    var chartContainer = d3.select(chartConfig.container);
    var w = (chartConfig.width || chartContainer.style('width')).replace('px', '');
    var h = (chartConfig.height || chartContainer.style('height')).replace('px', '');
    var outerPadding = chartConfig.outerPadding || 6;
    var extraLabelMargin = chartConfig.extraLabelMargin || 0;
    //rightmost border of container (used for tooltip clipping)
    var rightBorder = chartContainer.node().getBoundingClientRect().right;
    h = Number(h);
    extraLabelMargin = Number(extraLabelMargin);

    // Data
    var dataset = chartConfig.data || [{
      'date': '2013-06-06T00:00:00.000Z',
      'total': 10,
      'exactMatches': 10,
      'emphasize': true
    }, {
      'date': '2013-09-07T00:00:00.000Z',
      'total': 1,
      'exactMatches': 0,
      'emphasize': false
    }, {
      'date': '2013-10-02T00:00:00.000Z',
      'total': 3,
      'exactMatches': 0,
      'emphasize': false
    }, {
      'date': '2014-02-01T00:00:00.000Z',
      'total': 1,
      'exactMatches': 0,
      'emphasize': false
    }, {
      'date': '2014-03-05T00:00:00.000Z',
      'total': 2,
      'exactMatches': 0,
      'emphasize': false
    }, {
      'date': '2014-04-08T00:00:00.000Z',
      'total': 30,
      'exactMatches': 15,
      'emphasize': true
    }, {
      'date': '2014-05-09T00:00:00.000Z',
      'total': 5,
      'exactMatches': 0,
      'emphasize': false
    }];

    // Time Axis Scale
    var firstDate = chartConfig.firstDate || new Date(moment.utc(dataset[0].date));
    var lastDate = chartConfig.lastDate || new Date(moment.utc(dataset[dataset.length - 1].date));
    var xScale = d3.time.scale.utc().domain([firstDate, d3.time.day.offset(lastDate, 1)]).nice(d3.time.month).range([outerPadding, w - outerPadding]);

    // Time Axes
    var xAxisMonth = d3.svg.axis().scale(xScale).orient('bottom').ticks(d3.time.month).tickSize(7).tickFormat('');
    var xAxisMonthTop = d3.svg.axis().scale(xScale).orient('top').ticks(d3.time.month).tickSize(7).tickFormat(d3.time.format('%b'));
    var xAxisWeek = d3.svg.axis().scale(xScale).orient('bottom').ticks(d3.time.week).tickSize(5).tickFormat(d3.time.format('%_d'));

    // Circle Scale
    var maxInteractions = chartConfig.maxInteractions || d3.max(dataset, function (d) {
      return d.total;
    });
    var rScale = d3.scale.linear().domain([0, maxInteractions]).range([2, h * 0.5]); // <-- fix radius clipping here

    // Clear out container
    chartContainer.html(null);

    // Set up tooltip
    var tooltip = chartContainer.style('position', 'relative').append('div').attr('class', 'd3tooltip has-header').append('span').attr('class', 'value');

    // Set up chart container
    var svgContainer = chartContainer.append('svg').attr('width', w).attr('height', h + extraLabelMargin).attr('class', 'd3-chart interaction-chart');

    // Draw axes
    var monthAxisBottom = svgContainer.append('g').attr('class', 'month-axis-bottom axis').attr('transform', 'translate(0, ' + h / 2 + ')').call(xAxisMonth);
    var monthAxisTop = svgContainer.append('g').attr('class', 'month-axis-top axis').attr('transform', 'translate(0, ' + h / 2 + ')').call(xAxisMonthTop).selectAll(".tick text").style("text-anchor", "start").attr("x", 5).attr("y", -4);
    var weekAxis = svgContainer.append('g').attr('class', 'week-axis axis').attr('transform', 'translate(0, ' + h / 2 + ')').call(xAxisWeek);

    // Draw big circles
    var bigCircles = svgContainer.append('g').attr('class', 'big-circles').selectAll('circle').data(dataset).enter().append('circle').attr('cx', function (d) {
      return xScale(new Date(moment.utc(d.date)));
    }).attr('cy', function (d) {
      return h / 2;
    }).attr('r', function (d) {
      return rScale(d.total);
    }).attr('class', function (d) {
      if (d.emphasize) {
        return 'emphasize';
      }
    });
    if (chartConfig.colors && chartConfig.colors[0]) {
      bigCircles.attr('style', 'fill:' + chartConfig.colors[0]);
    }

    // Configurable tool tip label
    var tooltipLabel = chartConfig.tooltipLabel || function (d) {
      var dateDisplay = moment.utc(d.date).format("MMMM DD, YYYY"),
        exactMatches = d.exactMatches,
        related = d.total - d.exactMatches,
        relatedLabel = related + (related === 1 ? ' related interaction' : ' related interactions'),
        relatedSwatch = "<span class=\"no-match\">&nbsp;\u2587 </span>",
        exactMatchSwatch = "<span class=\"match\">&nbsp;\u2587 </span>",
        exactMatchLabel;
      if (chartConfig.getMatchLabel) {
        exactMatchLabel = chartConfig.getMatchLabel(exactMatches);
      } else {
        exactMatchLabel = exactMatches + (exactMatches === 1 ? ' matched interaction' : ' matched interactions');
      }
      return '<span class="header">' + dateDisplay + '</span>' + (exactMatches > 0 ? '<br>' + exactMatchSwatch + exactMatchLabel : '') + (related > 0 ? '<br>' + relatedSwatch + relatedLabel : '');
    };

    // Tooltip stuff
    var d3tooltip = chartContainer.select('.d3tooltip');
    bigCircles = bigCircles.on('mouseover', function (d) {
      var coordinates = d3.mouse(this);
      var xPosition = coordinates[0];
      var yPosition = coordinates[1] - 40;
      var windowMouse = d3.mouse(d3.select('body').node());
      var windowMouseX = windowMouse[0];
      var windowWidth = window.innerWidth;
      d3tooltip.select('.value').html(tooltipLabel(d));
      d3tooltip.style('left', function () {
        var tooltipWidth = parseInt(d3tooltip.style('width'));
        //check if the window or the chart container will clip the tooltip
        var tooltipClip = windowMouseX + tooltipWidth > Math.min(windowWidth, rightBorder);
        if (tooltipClip) {
          xPosition = xPosition - tooltipWidth;
        }
        return xPosition + 'px';
      }).style('top', yPosition + 'px');
      d3tooltip.transition().style('opacity', 1);
    }).on('mouseout', function () {
      d3tooltip.transition().style('opacity', 0);
    });

    // Draw little circles
    var littleCircles = svgContainer.append('g').attr('class', 'little-circles').selectAll('circle').data(dataset).enter().append('circle').attr('cx', function (d) {
      return xScale(new Date(moment.utc(d.date)));
    }).attr('cy', function (d) {
      return h / 2;
    }).attr('r', function (d) {
      return 2;
    });
  }
  function renderMatching(chartConfig) {
    /* Configurables:
     * - chartConfig.container (string or element)
     * - chartConfig.width (number: pixels)
     * - chartConfig.outerPadding (number: pixels)
     * - chartConfig.data (object with field 'match' (string) and
     * 'targets' (array of objects with fields 'name'(string), 'match'(boolean), 'answers'(number)))
     * - chartConfig.surveyMode (boolean)
     * - chartConfig.tooltipLabel (function - input of data point from targets, output of string)
     */

    // Global variables and whatnot
    var chartContainer = d3.select(chartConfig.container);
    var w = chartContainer.style('width').replace('px', '');
    var transDuration = 500;
    var outerPadding = chartConfig.outerPadding || 10;
    var linePadding = 5;
    var targetOffset = 20;
    var d3tooltipDistance = 6;
    var maxLabelCharacters = 30;
    var numberFormat = d3.format(","); // adds "," between every 3 digits

    function truncateLabel(label) {
      if (label && label.length > maxLabelCharacters) {
        return label.substring(0, maxLabelCharacters - 3) + '...';
      } else {
        return label;
      }
    }
    if (!w) {
      return;
    }
    var c1w = w * .25;
    var c2w = w * .25;
    var c3w = w * .50;
    var c1x = outerPadding;
    var c1y = outerPadding + 25;
    var c2x = c1w;
    var c2y = outerPadding + 25;
    var c3x = c1w + c2w;
    var c3y = outerPadding;
    var barHeight = 12;

    // Data
    var data = chartConfig.data || {
      'match': 'Bob\'s Team',
      'targets': [{
        'name': 'Rockets',
        'match': false,
        'answers': 2,
        'answerPercent': 5
      }, {
        'name': 'Green Hornets',
        'match': false,
        'answers': 2,
        'answerPercent': 10
      }, {
        'name': 'Super Cali Fragi Licious Expi Aladocious Super Cali Fragi Licious Expi Aladocious',
        'match': true,
        'answers': 196,
        'answerPercent': 70
      }, {
        'name': 'Yoga Masters',
        'match': false,
        'answers': 2,
        'answerPercent': 15
      }]
    };

    // Clear out container
    chartContainer.html(null);

    // Set up tooltip
    var tooltip = chartContainer.style('position', 'relative').append('div').attr('class', 'd3tooltip').append('span').attr('class', 'value');

    // Set up chart container
    var svgContainer = chartContainer.append('svg').attr('width', w).attr('class', 'd3-chart matching-chart');
    if (chartConfig.surveyMode) {
      chartContainer.attr('class', 'd3-chart matching-chart survey-mode');
    }

    // Add a background
    var background = svgContainer.append('g').attr('class', 'background').append('rect').attr('x', 0).attr('y', 0).attr('width', w);

    // Add the match label
    var matchLabel = svgContainer.append('g').attr('class', 'match-label').append('text').attr('x', c1x).attr('y', c1y).text(function () {
      return truncateLabel(data.match);
    });

    // Add the target labels
    var targetLabels = svgContainer.append('g').attr('class', 'target-labels').selectAll('text').data(data.targets).enter().append('text').attr('class', function (d) {
      if (d.match) {
        return 'match';
      }
    }).text(function (d) {
      return truncateLabel(d.name);
    }).attr('x', function (d, i) {
      var bbox = this.getBBox();
      var xPos = c2w - bbox.width + c1w - outerPadding;
      return xPos;
    }).attr('y', function (d, i) {
      var currentOffset = i * targetOffset;
      return c2y + currentOffset;
    });

    // Figure out where to drawn lines from and to for the correct match
    var matchBBox = matchLabel.node().getBBox();
    var targetMatchBBox;
    var targetBBoxes = [];
    targetLabels.each(function (d) {
      var selection = d3.select(this);
      targetBBoxes.push(this.getBBox());
      if (selection.classed('match')) {
        targetMatchBBox = this.getBBox();
      }
    });

    // Determine is any of the target labels bleed over into the space occupied by the match label
    var matchTotalWidth = matchBBox.width + matchBBox.x;
    var matchBleed = false;
    for (var i = 0; i < targetBBoxes.length; i++) {
      if (targetBBoxes[i].width + matchTotalWidth >= c1w + c2w) {
        matchBleed = true;
      }
    }

    // Move the match label up if there isn't enough room for it and the target labels to be side by side
    if (matchBleed) {
      matchLabel.attr('transform', 'translate(0, ' + -16 + ')');
    }

    // If there's enough room, draw lines
    if (!matchBleed) {
      // Set up line start
      var matchLineStartX = matchBBox.x + matchBBox.width + linePadding;
      var matchLineStartY = matchBBox.y + matchBBox.height / 2;

      // Draw the incorrect match lines
      var incorrectLines = svgContainer.append('g').attr('class', 'match-lines').selectAll('line').data(data.targets).enter().append('line').attr('x1', matchLineStartX).attr('y1', matchLineStartY).attr('x2', function (d, i) {
        return targetBBoxes[i].x - linePadding;
      }).attr('y2', function (d, i) {
        return targetBBoxes[i].y + targetBBoxes[i].height / 2;
      });

      // Draw the correct match lines
      var correctLines = svgContainer.append('g').attr('class', 'correct-match-lines').selectAll('line').data(data.targets).enter().append('line').style('opacity', function (d) {
        return d.match ? 1 : 0;
      }).attr('x1', matchLineStartX).attr('y1', matchLineStartY).attr('x2', matchLineStartX).attr('y2', matchLineStartY).transition().duration(transDuration).attr('x2', function (d, i) {
        return targetBBoxes[i].x - linePadding;
      }).attr('y2', function (d, i) {
        return targetBBoxes[i].y + targetBBoxes[i].height / 2;
      });
    }

    // Scale for bar chart
    var xScale = d3.scale.linear().domain([0, 100]).range([0, c3w - outerPadding - linePadding]);

    // Draw the axis
    var axis = d3.svg.axis().scale(xScale).orient('top').tickFormat(function (d) {
      var suffix = '%';
      if (d !== 0) {
        suffix = '';
      }
      return d + suffix;
    }).ticks(5).tickSize(0);
    var xAxisTop = svgContainer.append('g').attr('class', 'top-axis axis').attr('transform', 'translate(' + c3x + ', ' + (c3y + 10) + ')').call(axis).selectAll('text').style('text-anchor', function (d) {
      var anchor = 'middle';
      if (d === 0) {
        anchor = 'start';
      } else if (d === 100) {
        anchor = 'end';
      }
      return anchor;
    });
    var longTickSize = targetBBoxes.length * 20;
    var axis2 = d3.svg.axis().scale(xScale).orient('bottom').tickFormat('').ticks(5).tickSize(longTickSize);
    var xAxisLines = svgContainer.append('g').attr('class', 'bottom-axis axis').attr('transform', 'translate(' + c3x + ', ' + (c3y + 10) + ')').call(axis2);

    // Draw the match bars
    var targetBars = svgContainer.append('g').attr('class', 'target-bars').selectAll('rect').data(data.targets).enter().append('rect').attr('class', function (d) {
      if (d.match) {
        return 'match';
      }
    }).attr('x', c3x).attr('y', function (d, i) {
      return targetBBoxes[i].y + targetBBoxes[i].height / 2 - barHeight / 2;
    });

    // Configurable tool tip label
    var tooltipLabel = chartConfig.tooltipLabel || function (data) {
      var swatch = data.match ? "<span class=\"match\">\u2587 </span>" : "<span class=\"no-match\">\u2587 </span>",
        filler = "<span style=\"color:rgba(255, 255, 255, 0.0)\">\u2587 </span>",
        verb = chartConfig.surveyMode ? 'matched ' : data.match ? ' correctly matched ' : 'incorrectly matched ';
      return swatch + data.answerPercent + '% ' + verb + '<strong>' + truncateLabel(data.name) + '</strong>' + '<br>' + filler + '(' + numberFormat(data.answers) + (data.answers === 1 ? ' attempt)' : ' attempts)');
    };

    // Tooltip handling
    var d3tooltip = chartContainer.select('.d3tooltip'),
      chartW = 0,
      chartH = 0,
      tooltipWidth = 0,
      tooltipHeight = 0,
      tooltipWidthPlusOffset = 0,
      tooltipHeightPlusOffset = 0,
      tooltipRightLimit = 0,
      tooltipBottomLimit = 0,
      tooltipHalfWidth = 0,
      tooltipHalfHeight = 0;
    targetBars = targetBars.on('mouseover', function (d) {
      d3tooltip.style('opacity', 1.0);
      d3tooltip.select('.value').html(tooltipLabel(d));
      chartW = chartContainer.style('width').replace('px', ''), chartH = chartContainer.style('height').replace('px', ''), tooltipWidth = parseInt(d3tooltip.style('width'));
      tooltipHeight = parseInt(d3tooltip.style('height'));
      tooltipWidthPlusOffset = tooltipWidth + d3tooltipDistance;
      tooltipHeightPlusOffset = tooltipHeight + d3tooltipDistance;
      tooltipRightLimit = chartW - tooltipWidth;
      tooltipBottomLimit = chartH - tooltipHeight;
      tooltipHalfWidth = tooltipWidth / 2;
      tooltipHalfHeight = tooltipHeight / 2;
    }).on('mousemove', function () {
      var coordinates = d3.mouse(this),
        xPosition = coordinates[0] + d3tooltipDistance,
        // position tooltip to the right of mouse
        yPosition = coordinates[1] - tooltipHalfHeight; // centered vertically
      if (xPosition > tooltipRightLimit) {
        // if too far right,
        xPosition = coordinates[0] - tooltipWidthPlusOffset; // try left of mouse
      }
      if (xPosition < 0) {
        // if too far left, center within left & right limits
        xPosition = coordinates[0] - tooltipHalfWidth;
        xPosition = limitValue(xPosition, {
          min: 0,
          max: tooltipRightLimit
        });
        yPosition = coordinates[1] + d3tooltipDistance; // position tooltip below cursor
        if (yPosition > tooltipBottomLimit) {
          // if too low, position above cursor
          yPosition = Math.max(0, coordinates[1] - tooltipHeightPlusOffset); // limited to top of chart
        }
      } else {
        // horizontal ok, make sure vertical limits are enforced
        yPosition = limitValue(yPosition, {
          min: 0,
          max: tooltipBottomLimit
        });
      }
      d3tooltip.transition().duration(200).ease('exp-out').style('left', xPosition + 'px').style('top', yPosition + 'px');
    }).on('mouseout', function () {
      d3tooltip.style('opacity', 0);
    });
    targetLabels = targetLabels.on('mouseover', function (d) {
      if (d.name.length > maxLabelCharacters) {
        var coordinates = d3.mouse(this);
        var xPosition = coordinates[0];
        var yPosition = coordinates[1] - d3tooltipDistance;
        d3tooltip.transition().style('opacity', 1.0);
        var windowMouse = d3.mouse(d3.select('body').node());
        var windowMouseX = windowMouse[0];
        var windowWidth = window.innerWidth;
        var tooltipWidth = parseInt(d3tooltip.style('width'));
        var tooltipClip = windowMouseX + tooltipWidth > windowWidth;
        d3tooltip.style('left', function (d) {
          if (tooltipClip) {
            xPosition = xPosition - tooltipWidth;
          }
          return xPosition + 'px';
        }).style('top', yPosition + 'px').select('.value').text(d.name);
      }
    }).on('mouseout', function () {
      d3tooltip.transition().style('opacity', 0);
    });
    matchLabel = matchLabel.on('mouseover', function (d) {
      if (data.match.length > maxLabelCharacters) {
        var coordinates = d3.mouse(this);
        var xPosition = coordinates[0];
        var yPosition = coordinates[1] - d3tooltipDistance;
        d3tooltip.transition().style('opacity', 1.0);
        var windowMouse = d3.mouse(d3.select('body').node());
        var windowMouseX = windowMouse[0];
        var windowWidth = window.innerWidth;
        var tooltipWidth = parseInt(d3tooltip.style('width'));
        var tooltipClip = windowMouseX + tooltipWidth > windowWidth;
        d3tooltip.style('left', function (d) {
          if (tooltipClip) {
            xPosition = xPosition - tooltipWidth;
          }
          return xPosition + 'px';
        }).style('top', yPosition + 'px').select('.value').text(data.match);
      }
    }).on('mouseout', function () {
      d3tooltip.transition().style('opacity', 0);
    });

    // Animations
    targetBars = targetBars.attr('width', 0).transition().delay(transDuration).duration(transDuration).attr('width', function (d) {
      return xScale(d.answerPercent);
    }).attr('height', barHeight);

    // Set chart and background height based on data, padding, offsets, etc.
    var h = data.targets.length * targetOffset + outerPadding * 2 + chartContainer.select('.top-axis').node().getBBox().height + 'px';
    chartContainer.select('svg').attr('height', h);
    chartContainer.select('.background rect').attr('height', h);
  }
  function renderNetwork(chartConfig) {
    var width = chartConfig.width || $(chartConfig.container).width() || 1200,
      height = chartConfig.height || 800,
      showLabels = true,
      nodes = chartConfig.nodes,
      links = chartConfig.links;
    var force = d3.layout.force().linkDistance(80).charge(-120).gravity(.05).size([width, height]).on("tick", tick);
    var svg = d3.select(chartConfig.container).append("svg").attr("width", width).attr("height", height);
    var link = svg.selectAll(".link"),
      node = svg.selectAll(".node");
    update();

    // Set up node tooltip
    // var d3Tooltip = d3.select("body").append("div")
    //     .attr("class", "network-graph-tooltip")
    //     .style("opacity", 0);

    function update() {
      svg.selectAll("circle").remove();
      svg.selectAll("text").remove();
      svg.selectAll(".link").remove();
      link = svg.selectAll(".link").data(links);
      link.enter().append("line").attr("class", "link").style("stroke-width", function (d) {
        return Math.sqrt(d.weight);
      });
      link.exit().remove();
      node = node.data(nodes);
      var nodeEnter = node.enter().append("g").attr("class", "node")
      // .on("mouseover", function(d) {
      //     d3Tooltip.transition()
      //         .style("opacity", .9);
      //     d3Tooltip.html('<b>' + d.name + '</b>')
      //         .style("left", (d3.event.pageX) + "px")
      //         .style("top", (d3.event.pageY - 28) + "px");
      // })
      // .on("mouseout", function(d) {
      //     d3Tooltip.transition()
      //         .duration(500)
      //         .style("opacity", 0);
      // })
      .on("click", clickToggle).call(force.drag);
      node.append("circle").attr("r", function (d) {
        return Math.sqrt(d.size || 1000) / 10;
      });
      node.append("text")
      //.style("transform", "translate(0px, -10px)")
      .text(function (d) {
        return d.name;
      });
      node.select("circle").style("fill", color);
      node.exit().remove();

      // Move all nodes to foreground
      d3.selectAll(".node").each(function () {
        this.parentNode.appendChild(this);
      });
      force.nodes(nodes).links(links).start();
    }
    function tick() {
      link.attr("x1", function (d) {
        return d.source.x;
      }).attr("y1", function (d) {
        return d.source.y;
      }).attr("x2", function (d) {
        return d.target.x;
      }).attr("y2", function (d) {
        return d.target.y;
      });
      node.attr("transform", function (d) {
        return "translate(" + d.x + "," + d.y + ")";
      });
    }
    function color(d) {
      if (d.color) {
        return d.color;
      }
      return d._children ? "#3182bd" // collapsed package
      : d.children ? "#c6dbef" // expanded package
      : "#fd8d3c"; // leaf node
    }
    function clickToggle(d) {
      if (d3.event.defaultPrevented) return; // ignore drag

      var keepNodes = [];
      links = links.filter(function (l) {
        if (l.source === d || l.target === d) {
          keepNodes.push(l.source);
          keepNodes.push(l.target);
          return true;
        }
        return false;
      });
      nodes = nodes.filter(function (n) {
        return keepNodes.indexOf(n) > -1;
      });
      d3.event.stopPropagation();
      update();
    }

    // Find and click a node programmatically
    chartConfig.findNode = function findNode(userInput) {
      var keepNodes = [];
      links = links.filter(function (l) {
        if (l.source.id === 'root' || l.target.id === 'root') {
          return false;
        }
        if (l.source.name && l.source.name.toLowerCase().indexOf(userInput) > -1 || l.target.name && l.target.name.toLowerCase().indexOf(userInput) > -1) {
          keepNodes.push(l.source);
          keepNodes.push(l.target);
          return true;
        }
        return false;
      });
      nodes = nodes.filter(function (n) {
        return n.id !== 'root' && keepNodes.indexOf(n) > -1;
      });
      update();
    };
    chartConfig.expandAll = function expandAll() {
      var opacity = showLabels ? 1 : 0;
      d3.selectAll("text").style("opacity", opacity);
      nodes = this.nodes;
      links = this.links;
      update();
    };

    // Toggle labels
    chartConfig.toggleLabels = function toggleLabels() {
      showLabels = !showLabels;
      var opacity = showLabels ? 1 : 0;
      d3.selectAll("text").style("opacity", opacity);
    };
    chartConfig.resize = function () {
      var chartContainer = d3.select(chartConfig.container);
      var ew = chartContainer.style('width').replace('px', '');
      var eh = chartContainer.style('height').replace('px', '');
      chartContainer.select('svg').attr("width", ew).attr("height", eh);
      update();
    };
  }

  // Export
  var watershed = global.watershed || {};
  watershed.charts = watershed.charts || {};
  watershed.charts.render = render;
  global.watershed = watershed;
})(this);
